import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Container, Typography } from "@mui/material";
import { loadCheckoutWebComponents } from "@checkout.com/checkout-web-components";
import { postRequest, clearSessionStorage } from "../../services/httpservice";
import { apiURL } from "../../constants/apiUrlConstants";
import axios, { AxiosError } from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Loader from "../../components/Loader";

function Checkout() {
  const navigate = useNavigate();
  const flowContainerRef = useRef<HTMLDivElement | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const sessionData = JSON.parse(sessionStorage.getItem("checkoutData") as string);
  const loanAccountNo = sessionStorage.getItem("checkoutLoanAccountNo") || "";

  useEffect(() => {
    if (!sessionData?.sessionResponse) {
      toast.error("Invalid session. Please try again.");
      navigate(-1);
      return;
    }

    let flowComponent: any = null;
    let cancelled = false;

    const init = async () => {
      try {
        if (!flowContainerRef.current) return;

        const checkout = await loadCheckoutWebComponents({
          publicKey: process.env.REACT_APP_CHECKOUT_PUBLIC_KEY as string,
          environment: process.env.REACT_APP_CHECKOUT_BASE_URL as any,
          locale: "en-GB",
          paymentSession: sessionData.sessionResponse,

          onReady: () => {
            setIsLoading(false);
          },

          onPaymentCompleted: async (_component: any, paymentResponse: any) => {
            sessionStorage.removeItem("checkoutData");
            sessionStorage.removeItem("checkoutLoanAccountNo");
            navigate("/home/payment-status");
          },

          onError: (_component: any, _error: any) => {
            setIsLoading(false);
          },
        });

        if (cancelled) return;

        flowComponent = checkout.create("flow", {
          // onTokenized: (_self: any, tokenizeResult: any) => onTokenized(tokenizeResult),
        });

        flowComponent.mount(flowContainerRef.current);
      } catch (e: any) {
        setIsLoading(false);
        toast.error(e?.message || process.env.REACT_APP_ERROR_MSG);
      }
    };

    init();

    return () => {
      cancelled = true;
      try { flowComponent?.unmount?.(); } catch { }
    };
  }, []);

  const onTokenized = async (tokenizeResult: any) => {
    try {
      setIsLoading(true);
      const civilId = sessionStorage.getItem("civilId");
      const response = await postRequest(
        process.env.REACT_APP_API_URL + apiURL.VALIDATE_CARD,
        { ...tokenizeResult, loanAccountNo, civilId }
      );
      setIsLoading(false);
      if (response == null || response.data === "") {
        toast.error(process.env.REACT_APP_ERROR_MSG);
        return;
      }
      if (response.status === 401) {
        toast.error("Session expired");
        clearSessionStorage();
        setTimeout(() => { navigate("/"); }, 2000);
        return;
      }
      const data = response.data;
      if (data.status?.toLowerCase() === "s") {
        return { continue: true };
      } else {
        return { continue: false, errorMessage: data.message || "Card not supported" };
      }
    } catch (error) {
      setIsLoading(false);
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError;
        if (axiosError?.response?.status === 401) {
          toast.error("Session expired");
          clearSessionStorage();
          setTimeout(() => { navigate("/"); }, 2000);
        } else {
          toast.error(process.env.REACT_APP_ERROR_MSG);
        }
      }
    }
  };

  return (
    <>
      {isLoading && <Loader />}
      <Container maxWidth="sm" sx={{ mt: 4, mb: 6 }}>
        <Typography variant="h6" fontWeight={700} mb={3} color="#001B5E">
          Card Change Payment
        </Typography>
        <Box
          sx={{
            background: "#fff",
            borderRadius: "12px",
            boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
            p: 3,
            minHeight: 300,
          }}
        >
          <div ref={flowContainerRef} />
        </Box>
      </Container>
      <ToastContainer position="top-right" newestOnTop limit={1} />
    </>
  );
}

export default Checkout;
